import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import {
  catchError,
  switchMap,
  map,
  filter,
  take,
  finalize,
} from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthService } from '../services/Auth/auth.service';
import { BrowserStorageService } from '../services/browser-storage/browser-storage.service';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { PlayerService } from '../services/music-player/player.service';
import { MobileVerificationFlowService } from '../services/Auth/mobile-verification-flow.service';
import {
  MobileActionWallType,
  VerificationContractService,
} from '../services/Auth/verification-contract.service';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  isRefreshingToken: boolean = false;
  tokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private storageService: BrowserStorageService,
    private ngxLoadingService: NgxSpinnerService,
    private authService: AuthService,
    private router: Router,
    private location: Location,
    private verificationContract: VerificationContractService,
    private injector: Injector
  ) {}

  private updateDataCrossHeader(request: any) {
      const updatedHeaders = request.headers
          .set("Content-Security-Policy", "frame-ancestors 'self'")
          .set("X-Frame-Options", "SAMEORIGIN")
          .set("Cross-Origin-Opener-Policy", "same-origin")
        return request.clone({
          headers: updatedHeaders
        });
  }
  private updateToken(request: any, token: any) {
     const updatedHeaders = request.headers
          .set("Content-Security-Policy", "frame-ancestors 'self'")
          .set("X-Frame-Options", "SAMEORIGIN")
          .set("Cross-Origin-Opener-Policy", "same-origin")
          .set('Authorization', `Bearer ${token}`)
        return request.clone({
          headers: updatedHeaders
        });
  }

  isUserLogout() {
    this.storageService.clearAllSession();
    this.authService.currentUserDetails.next({});
    this.router.navigate(['/signin']);
    this.authService.closeAllDialogBox.next(true);
    setTimeout(() => {
      location.reload();
    },1000);
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const token: any = this.storageService.getSessionData();
    let authReq = request;
    if (
      token != null &&
      !authReq.url.includes('auth/refresh') &&
      !authReq.url.includes('amazonaws.com')
    ) {
      authReq = this.updateToken(request, token?.access_token);
    } else {
      if (!authReq.url.includes('amazonaws.com')) {
        authReq = this.updateDataCrossHeader(request);
      }

    }
    return next.handle(authReq).pipe(
      map(event => {
        if (event instanceof HttpResponse) {
          this.ngxLoadingService.hide();
        }
        return event;
    }),
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          const wallType = this.getMobileActionWallType(error, authReq?.url || '');
          if (wallType) {
            const musicPlayerService = this.injector.get(PlayerService);
            const mobileVerificationFlow =
              this.injector.get(MobileVerificationFlowService);
            musicPlayerService.stop();
            mobileVerificationFlow.triggerFromError(
              error,
              wallType,
              wallType === 'download'
            );
          }
        }
        let errorMessage = '';
        if (error?.status === 401) {
          errorMessage = 'Invalid token! Please sign in again.';
        } else if (error?.status === 400) {
          errorMessage = 'Your token has expired! Please sign in again.';
        }
        if (
          error instanceof HttpErrorResponse &&
          !authReq.url.includes('auth/signin') &&
          !authReq.url.includes('auth/refresh') &&
          error?.error?.message === errorMessage
        ) {
          return this.handle401Error(authReq, next);
        }
        if (authReq.url.includes('auth/refresh')) {
          this.isUserLogout();
        }
        let messageError: any = error?.error?.message;
        if (messageError && (messageError.includes("Unable to find beat") ||  messageError.includes("Invalid playlist slug"))) {
          setTimeout(() => {
            this.location.back();
          },500);
        }
        this.ngxLoadingService.hide();
        return throwError(error);
      }),
      finalize(() => this.ngxLoadingService.hide())
    );
  }

  handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.tokenSubject.next(null);

      const token = this.storageService.getRefreshAccessToken();

      if (token)
        return this.authService.refreshToken(token).pipe(
          switchMap((token: any) => {
            this.isRefreshingToken = false;

            const sessionData: any = this.storageService.getSessionData();
            if (sessionData) {
              sessionData.access_token = token.access_token;
              sessionData.refresh_token = token.refresh_token;
              this.storageService.setSessionData(sessionData);
              this.storageService.removedBpmDialogValue();
              this.tokenSubject.next(token.access_token);
            }

            return next.handle(this.updateToken(request, token.access_token));
          }),
          catchError((err) => {
            this.ngxLoadingService.hide();
            this.storageService.clearAllSession();
            this.isRefreshingToken = false;
            return throwError(err);
          })
        );
    }

    return this.tokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.updateToken(request, token)))
    );
  }

  private getMobileActionWallType(
    error: any,
    requestUrl: string
  ): MobileActionWallType | null {
    const wallType = this.verificationContract.getMobileActionWallType(error);
    if (!wallType) {
      return null;
    }

    if (wallType === 'play') {
      return wallType;
    }

    const normalizedUrl = (requestUrl || '').toLowerCase();
    const isDownloadRequest =
      normalizedUrl.includes('download') ||
      normalizedUrl.includes('read-object-url');
    if (!isDownloadRequest) {
      return null;
    }

    return wallType;
  }
}
